import { useMemo } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { isValidArtifact } from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { getInfoBoxConfig } from '@backrs_ui/grow-ui/src/components/PageShell/resourceTabs/InfoBox.config';
import { InfoBoxContent } from '@backrs_ui/grow-ui/src/components/PageShell/resourceTabs/InfoBoxContent';
import { useSortedResources } from '@backrs_ui/grow-ui/src/components/PageShell/resourceTabs/useSortedResources';
import { selectSelectedMemberSolutionSession } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/memberSolutionSession';
import { useGetOfficeHoursSessionDataQuery } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { hydrateSessionResources } from '@backrs_ui/grow-ui/src/utils/streamUtils';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { TabErrorMessage } from './MemberTabErrorState';
import { useAgentContext } from './SolutionSessionOutputView.agentContext';

const {
  modules: { coachingDashboard },
} = appStrings;

export const SolutionSessionOutputView = () => {
  const session = useAppSelector(selectSelectedMemberSolutionSession);

  const { data, isLoading, isError } = useGetOfficeHoursSessionDataQuery(
    session ? { session_id: session.sessionId } : skipToken,
  );

  const featuredBox = useMemo(
    () => (session ? getInfoBoxConfig(null, session.sessionType).FEATURED : undefined),
    [session],
  );

  const resources = useMemo(
    () =>
      session && data?.additional_resources
        ? hydrateSessionResources(
            data.additional_resources,
            session.sessionType,
            data.featured_nugget_types,
          )
        : undefined,
    [data, session],
  );

  const sortedContent = useSortedResources(featuredBox, resources, 'FEATURED');

  const artifact = useMemo(() => {
    if (!data?.artifact) return undefined;

    if (isValidArtifact(data.artifact)) return data.artifact;

    console.warn('Dropping session artifact with unrecognized kind', data.artifact);

    return undefined;
  }, [data]);

  useAgentContext(session);

  if (!session) return null;

  if (isError) {
    return <TabErrorMessage message={coachingDashboard.solutionSessionOutputError} />;
  }

  return (
    <Box sx={containerSx}>
      <InfoBoxContent
        currentBox={featuredBox}
        sortedContent={sortedContent}
        isFeaturedTab
        isSuggestedTab={false}
        isLoading={isLoading}
        artifact={artifact}
        highlightedNugget={null}
        emptyStateContent={
          <Typography variant="text-sm-regular" sx={emptyStateSx}>
            {coachingDashboard.solutionSessionOutputEmpty}
          </Typography>
        }
      />
    </Box>
  );
};

const emptyStateSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingRight: backrs.spacing['spacing-md'],
});
